/**
 * Dicionário canônico (pt-BR). A FORMA deste objeto é o contrato: `en.ts` é tipado como
 * `Dictionary`, então o próprio TypeScript recusa chave faltando ou sobrando.
 *
 * `nav` e `metaAdsColumns` são mapas de SOBRESCRITA por chave estável (o título em PT do
 * item de menu; o `id` da coluna). Ficam vazios aqui de propósito: em pt-BR o valor certo
 * já está no código, e duplicá-lo criaria duas fontes de verdade para a mesma string.
 */
export const pt = {
    common: {
        cancel: "Cancelar",
        save: "Salvar",
        loading: "Carregando...",
        connect: "Conectar",
        disconnect: "Desconectar",
        activeSession: "Sessão Ativa",
        myAccount: "Minha Conta",
        adminSystem: "Administração & Sistema",
        signOut: "Sair do Sistema",
        default: "Padrão",
        connectedAccounts: "Contas conectadas",
        needsAttention: "Requer atenção",
        switchingStore: "Trocando de loja",
        storeSwitchFailed: "Não troquei de loja",
        storeSwitchFailedHint: "Tente de novo; se continuar, saia e entre de novo.",
        allGood: "Tudo em dia",
        last30Days: "Últimos 30 dias",
        activeCampaignsSum: "Somadas entre as contas",
        searchAccount: "Buscar conta, ID ou moeda...",
        language: "Idioma",
        portuguese: "Português",
        english: "English",
    },

    /** Sobrescrita por título do item de menu (chave = string em PT do sidebar). */
    nav: {} as Record<string, string>,

    billing: {
        planYearly: "anual",
        planMonthly: "mensal",
        day: "dia",
        days: "dias",
        trialExpired: (d: string) => `🔒 Acesso bloqueado: Trial expirado há ${d}. Seus pedidos continuam sendo recebidos, mas você precisa assinar para acessá-los.`,
        subscribeNow: "Assinar Agora",
        trialEndingCritical: "Evite o bloqueio da sua operação assinando um plano.",
        trialEndingSoft: "Assine um plano para continuar usando.",
        trialEnds: (d: string, tail: string) => `Seu período de teste finaliza em ${d}. ${tail}`,
        subscribePlanNow: "Assinar Plano Agora",
        renewsIn: (plan: string, d: string) => `Seu plano${plan} será renovado em ${d}. Certifique-se de que seus dados de pagamento estão atualizados.`,
        viewBilling: "Ver Faturamento",
        pastDue: "Falha no pagamento da sua assinatura. Atualize seus dados de pagamento para evitar a suspensão do serviço.",
        updatePayment: "Atualizar Pagamento",
        canceled: "Sua assinatura foi cancelada. Reative para continuar usando todos os recursos.",
        reactivatePlan: "Reativar Plano",
        unpaid: "Sua assinatura está suspensa por falta de pagamento. Regularize agora para restabelecer o acesso.",
        settlePayment: "Regularizar Pagamento",
    },

    /**
     * Rótulo de forma de pagamento por CHAVE do domínio (`paymentMethodKey`).
     *
     * Fonte única para as 6 superfícies (card do dashboard, detalhe do pedido, detalhe do
     * cliente, histórico de automação, tabela/planilha de pedidos e o CSV). Antes disto a
     * mesma forma de pagamento aparecia com três textos diferentes na mesma tela —
     * "Shopify Pay" na tabela, "Shopify Payments" na planilha e `shopify_payments` cru no
     * detalhe — e o card do dashboard normalizava só 3 casos, deixando 27,7% dos pedidos com
     * o rótulo cru da plataforma.
     *
     * Nome de marca não se traduz: "Shopify Payments" e "PayPal" são iguais em pt e en.
     */
    paymentMethod: {
        PIX: "Pix",
        CARD: "Cartão",
        BOLETO: "Boleto",
        SHOPIFY_PAYMENTS: "Shopify Payments",
        PAYPAL: "PayPal",
        OTHER: "Outros",
        UNKNOWN: "Não identificado",
    },

    charts: {
        noData: "Sem dados",
        // O recorte fica NA COPY porque o gráfico exclui Pix e cancelados
        // (`dashboard-metrics.service.ts:425-430`) enquanto o card vizinho conta tudo — medido em
        // 19/09/2026: 141 contra 223 na mesma tela. Sem dizer, o lojista vê dois totais
        // diferentes lado a lado e nada que explique a diferença. E "pagos" era falso: o filtro
        // só tira CANCELED, então pedido pendente entrava.
        installments: "Parcelamentos",
        installmentsSub: "Parcelas — exclui Pix e cancelados",
        installmentsRange: "5x ou mais",
        centerOrders: "pedidos",
        paymentMethods: "Formas de Pagamento",
        paymentMethodsSub: "Distribuição por método — todos os pedidos",
        topCustomers: "Top Clientes VIP",
        topCustomersSub: "Maior LTV no período",
        noCustomers: "Nenhum cliente no período",
        topProducts: "Top 5 Produtos",
        topProductsSub: "Mais vendidos no período",
        noProducts: "Sem dados de produtos",
        noSales: "Sem dados de vendas no período",
        dailyTotal: "Total Diário",
        ordersByOrigin: "Volume de pedidos por origem",
    },

    sync: {
        allIntegrations: "todas as integrações",
        started: "Sincronização iniciada!",
        startedBody: "Você será notificado quando os dados forem atualizados.",
        // Título PRÓPRIO para o caminho em que nada foi enfileirado. Reusar `started`
        // ("Sincronização iniciada!") fazia o título contradizer o corpo — e o lojista lê o
        // título, fecha o toast e fica esperando pedidos que não vêm por sync.
        nothingToSync: "Nada a sincronizar",
        errorTitle: "Erro",
        nothingToSyncBody:
            "Nenhuma plataforma conectada puxa histórico — as vendas dessas plataformas chegam automaticamente, em tempo real.",
        syncAll: "Sincronizar Todas",
        syncOne: (nome: string) => `Sincronizar ${nome}`,
        inQueue: "Sincronização em andamento na fila. Por favor, aguarde...",
        sync: "Sincronizar",
        startedTitle: "Sincronização Iniciada",
        startedLong: "Sua solicitação foi enviada com sucesso! Você já pode fechar esta janela e continuar navegando. Os novos dados aparecerão no seu painel em alguns instantes.",
        close: "Fechar",
        manualTitle: "Sincronização Manual",
        descAll: (nome: string) => `Busca pedidos de ${nome} ativas para o período selecionado.`,
        descOne: (nome: string) => `Busca os pedidos mais recentes da ${nome} para o período selecionado.`,
        periodAll: "Período: Todas as datas",
        period: (intervalo: string) => `Período: ${intervalo}`,
        until: "até",
        starting: "Iniciando...",
        availableIn: (s: string) => `Disponível em ${s}s`,
        start: "Iniciar Sincronização",
    },

    chat: {
        userList: "Lista de Usuários",
        roomStart: "Início desta sala",
        sayHi: "Diga oi para a equipe!",
        retention: (d: string) => `As mensagens deste chat são mantidas por ${d} dias por segurança.`,
        internalNote: "Conversa interna — visível apenas para a equipe",
    },

    currency: {
        title: "Câmbio",
        vsFriday: "vs. fechamento de sexta-feira",
        vsYesterday: "vs. fechamento de ontem",
        refresh: "Atualizar câmbio",
    },

    chrome: {
        helpCenter: "Central de Ajuda",
        helpOverview: "Visão geral",
        helpDocs: "Documentação",
        notifications: "Notificações",
        fullScreen: "Ver em tela cheia",
        navMenu: "Menu de Navegação",
        supportMode: "MODO SUPORTE (GOD MODE) ATIVO: Todas as ações realizadas nesta sessão estão sendo registradas no Audit Log.",
    },

    // B.3 — aviso ao lojista quando o provedor de rastreio da Mulply está fora do ar. Namespace
    // PLANO de propósito: `dictionaries.test.ts` exige string/função no 1º nível de cada namespace.
    // Em 1ª pessoa ("Mulply"): neste produto, "plataforma" é a loja do lojista (Shopify etc.).
    tracking: {
        unavailableTitle: "O rastreio automático da Mulply está fora do ar",
        unavailableBody: "Seus pedidos continuam na fila e voltam a ser atualizados quando o serviço voltar. Você não precisa fazer nada, nem abrir chamado.",
        unavailableLink: "Ver detalhes",
    },
    login: {
        title: "Bem-vindo de volta",
        subtitle: "Acesse seu painel de operações",
        email: "Email",
        password: "Senha",
        signIn: "Entrar",
        signingIn: "Entrando...",
        forgotPassword: "Esqueceu?",
        hidePassword: "Ocultar senha",
        showPassword: "Mostrar senha",
        noAccount: "Não tem uma conta?",
        signUp: "Criar conta",
        signInGoogle: "Entrar com Google",
        signInFacebook: "Entrar com Facebook",
        divider: "Ou use seu email",
        emailPlaceholder: "seu@email.com",
        codeLabel: "Código de verificação",
        lostDevice: "Perdeu o aparelho? Use um dos seus códigos de recuperação.",
        backToEmail: "Voltar para Email",
        firstTime: "Primeira vez aqui?",
        createAccount: "Crie sua conta",
        accessGranted: "Acesso autorizado…",
        terms: "Termos",
        privacy: "Privacidade",
        support: "Suporte",
        twoFactorTitle: "Autenticação em Duas Etapas",
        twoFactorSubtitle: "Digite o código do seu app autenticador",
        verify: "Verificar",
        // Muro de tentativas. Fato + o que fazer, sem culpar o lojista nem revelar o teto.
        tooManyAttempts: "Muitas tentativas de login. Aguarde 1 minuto e tente de novo.",
    },

    trafego: {
        startHere: "Começar aqui",
        startHereSub: "Conte como é a sua operação — as respostas calibram todas as análises",
        stepCaminho: "Caminho",
        stepVertical: "Operação",
        stepExpert: "Expert",
        stepMercado: "Mercado",
        stepPlataforma: "Plataforma",
        stepCheckout: "Checkout",
        stepNumeros: "Números",
        q1Title: "Você já roda tráfego hoje?",
        q1Sub: "Define seu ponto de partida no produto.",
        q1Yes: "Sim, já tenho campanhas",
        q1YesSub: "Vamos avaliar sua operação inteira: conta, criativos, pixel e vendas",
        q1No: "Não, quero começar do zero",
        q1NoSub: "Construtor Guiado monta oferta, criativos e campanha — em construção",
    },

    welcome: {
        chkIntegration: "Conectar Integração",
        chkIntegrationDesc: "Vincule sua conta Shopify ou Yampi para importar pedidos.",
        chkRoles: "Configurar Cargos",
        chkRolesDesc: "Defina o que sua equipe pode acessar no sistema.",
        chkSettings: "Ajustar Configurações",
        chkSettingsDesc: "Configure logo, nome e detalhes da sua loja.",
        chkTeam: "Convidar Equipe",
        chkTeamDesc: "Adicione seus colaboradores para começar a operar.",
        chkDone: (n: string) => `${n} concluídos`,
        badgeGuest: "Bem-vindo ao Mulply",
        badgeSuccess: "Central de Sucesso",
        hello: "Olá,",
        traveler: "viajante",
        subGuest: "Sua conta foi criada, mas ainda estamos preparando seu ambiente de trabalho.",
        subOwner: "Sua loja está quase pronta para decolar! Veja o que falta para completar sua configuração.",
        subMember: "Aqui estão algumas dicas para você dominar a operação da loja hoje.",
        cardGuest: "Aguardando Liberação",
        cardOwner: "Checklist de Configuração",
        cardMember: "Guia de Acesso Rápido",
        cardSubGuest: "Fale com o administrador para liberar seu acesso.",
        cardSubOwner: "Complete estes passos para extrair o máximo do sistema.",
        cardSubMember: "Links essenciais para sua rotina de trabalho.",
        nextStep: "Qual o próximo passo?",
        nextStepBody1: "Peça ao administrador para atribuir um",
        role: "Cargo",
        nextStepBody2: "ao seu perfil. Assim que salvo, seu acesso será liberado instantaneamente.",
        ctaTitle: "Sugestões ou Dúvidas?",
        ctaBody: "Nosso suporte está pronto para te ajudar a configurar sua operação logística.",
        helpCenter: "Central de Ajuda",
        exploreTitle: "Explorar Funcionalidades",
        exploreBody: "Clique para saber mais sobre cada módulo.",
        tutOrders: "Gestão de Pedidos",
        tutOrdersDesc: "Acompanhe suas vendas em tempo real, gerencie status e imprima etiquetas.",
        tutLogistics: "Logística Inteligente",
        tutLogisticsDesc: "Integração direta com transportadoras e rastreio automático.",
        tutFinance: "Financeiro & Métricas",
        tutFinanceDesc: "Dashboards detalhados de faturamento e performance.",
        tutTeam: "Equipe & Colaboração",
        tutTeamDesc: "Crie cargos customizados e gerencie sua equipe.",
    },

    dashboardHome: {
        title: "Dashboard",
        subtitle: "Visão completa das suas operações em tempo real",
        fetchError: "Falha ao buscar métricas",
        importStarted: "Iniciando Importação 2025...",
        importStartedBody: "Isso pode levar alguns minutos. Aguarde o aviso de conclusão.",
        importSuccess: "Sucesso!",
        importSuccessBody: (n: string) => `Importação de 2025 concluída. ${n} pedidos processados.`,
        apiError: "Erro na API",
        errorTitle: "Erro",
        importFailed: "Falha ao importar histórico.",
        history2025: "Histórico 2025",
        periodToday: "Hoje",
        periodYesterday: "Ontem",
        periodWeek: "Semana",
        periodMonth: "Mês",
        periodYear: "Ano",
        vsPrevPeriod: "vs período anterior",
        vsPrevDay: "vs dia anterior",
        vsPrevWeek: "vs semana anterior",
        vsPrevMonth: "vs mês anterior",
        kpiGrossSales: "Vendas Criadas",
        kpiPaidRevenue: "Receita Paga",
        kpiPaidRevenueMeta: "faturamento processado",
        kpiAvgTicket: "Ticket Médio",
        kpiAvgTicketMeta: "média por pedido",
        kpiAvgLtv: "LTV Médio",
        kpiAvgLtvMeta: "receita por cliente",
        kpiCancelRate: "Taxa Cancelam.",
        kpiCancelRateMeta: "sobre total pedidos",
        kpiPending: "Pendentes",
        kpiPendingNone: "Nenhum pedido aguardando",
        kpiPendingSome: "Pagos e aguardando envio",
        revenueChart: "Evolução de Receita",
        revenueChartSub: "Receita paga diária no período",
        funnel: "Funil de Conversão",
        funnelSub: "Performance de Fechamento",
        funnelCreated: "CRIADOS",
        funnelCreatedSub: "Volume Bruto",
        funnelConfirmed: "CONFIRMADOS",
        funnelConfirmedSub: "Pagos",
        conversionRate: "Taxa de Conversão",
    },

    integrations: {
        title: "Integrações",
        subtitle: "Gerencie conexões com plataformas de e-commerce, pagamentos e logística",
        requiresCampaignsModule: "Requer o módulo Campanhas e Automações",
        // Conexão sem autenticação de webhook + disparo pago habilitado. Duas versões porque
        // a Wbuy (`auth.tipo: "bearer"`) aceita entrega mesmo com o token salvo — mandá-la salvar
        // a credencial seria instrução falsa.
        // Fato + ação, no molde que a casa já usa para ISTO em `integration-registry.ts`:
        // "Preenchido, passamos a recusar qualquer chamada que não venha do Guru. Em branco, a
        // proteção continua sendo só o endereço secreto da URL."
        // A versão anterior descrevia um buraco NOSSO ("a plataforma não confere credencial") e
        // terminava em susto ("mensagens cobradas na sua conta"), sem dizer o que fazer.
        // "A URL é a chave" é o mesmo fato e já carrega a instrução.
        webhookOpenRiskFixable:
            "Hoje a proteção é só o endereço da URL. Salve a credencial em Configurar para aceitarmos só o que vier da plataforma.",
        webhookOpenRiskPermanent:
            "A Wbuy não assina os eventos, então a URL é a chave. Trate-a como senha e desligue a conexão quando não estiver em uso.",
        configure: "Configurar",
        webhookCopied: "URL do webhook copiada!",
        loadError: "Erro ao carregar integrações.",
        noPermissionStatus: "Você não tem permissão para alterar status.",
        statusUpdateFailed: "Não foi possível atualizar o status.",
        statusUpdateError: "Erro ao atualizar status.",
        removed: "Integração removida.",
        removeError: "Erro ao remover integração.",
        reclassifyFailed: "Não foi possível reclassificar.",
        movedToCheckouts: "Movida para Checkouts.",
        movedToPlatforms: "Movida para Plataformas.",
        noSeparateCheckout: "Nenhum checkout separado",
        connectToStart: "Conecte uma plataforma para começar.",
        noActiveIntegration: "Nenhuma integração ativa",
        notCountedInQuota: "Não conta na cota",
        markAsPlatform: "Isto é a plataforma de loja — mover",
        markAsCheckout: "Isto é um checkout separado — mover",
        available: "Integrações Disponíveis",
        connect: "Conectar",
        comingSoon: "Em breve",
        comingSoonBody: "Ainda não disponível — avise o suporte se você usa essa plataforma.",
        planLocked: "não está disponível neste plano. Garantia de centralização total em um único Hub profissional.",
        comingSoonTitle: "Em Breve",
        comingSoonApis: "Novas APIs em homologação. Aguarde o lançamento oficial.",
        salesIntegrations: "Integrações de Venda",
        salesIntegrationsBody: "Soma das abas Plataformas e Checkouts. Conexões inativas não contam.",
        storePlatforms: "Plataformas de loja",
        storePlatformsBody: "Onde seu catálogo vive. Se a venda fecha no checkout da própria plataforma, esta é a única conexão de que você precisa.",
        checkoutsBody: "Só para quem fecha a venda fora da plataforma de loja. Novas conexões nascem pelo",
        checkoutsBodyTail: "— lá você escolhe o papel, e aqui pode mover uma conexão que ficou na aba errada.",
        paymentGateways: "Gateways de Pagamento",
        logistics: "Gestão Logística",
        confirmDeleteTitle: "Confirmar Exclusão",
        confirmDeleteBody: "Tem certeza que deseja remover esta integração? A ação não pode ser desfeita e pode afetar a sincronização de pedidos.",
        confirmDeleteYes: "Sim, remover",
        abandonedCart: "Carrinho abandonado",
        moving: "Movendo...",
        removing: "Removendo...",
        platformsTabName: "Plataformas",
        supportVia: "O atendimento via",
        hubPrefix: "Hub",
        cartRealtime: "TEMPO REAL (WEBHOOK)",
        cartViaApi: "VIA API — A CADA 30 MIN",
        tabMetaAds: "Meta Ads",
    },

    metaAdsConnect: {
        galleryTitle: "Contas de anúncios Meta",
        gallerySubtitle:
            "Conecte a sua conta de anúncios do Meta para ver os relatórios de campanha dentro do Mulply.",
        connectButton: "Conectar com Facebook",
        connecting: "Aguardando o Facebook...",
        permissionNote:
            "Acesso somente de leitura aos relatórios de anúncios. Você pode desconectar quando quiser.",
        chooseAccount: "Escolha a conta de anúncios",
        addAccount: "Adicionar conta",
        statusConnected: "Conectada",
        statusSyncing: "Sincronizando...",
        statusNeedsReconnect: "Requer reconexão",
        reconnect: "Reconectar",
        sync: "Sincronizar",
        viewCampaigns: "Ver campanhas",
        openInAdsManager: "Abrir no Gerenciador de Anúncios",
        rename: "Renomear",
        defaultAccount: "Conta padrão",
        setDefault: "Definir como padrão",
        remove: "Remover conta",
        removed: "Conta desconectada",
        selectAccountTitle: "Selecionar conta de anúncios",
        selectAccountBody: "Escolha qual conta conectar ao Mulply.",
        renameTitle: "Renomear conta",
        renameBody: "Nome de exibição no painel — não altera nada na Meta.",
        renamePlaceholder: "Nome da conta",
        removeTitle: "Remover conta?",
        removeBodyBefore: "A conexão com",
        removeBodyAfter: "será removida do Mulply. Nada é alterado na sua conta Meta.",
        noAccountsFound: "Nenhuma conta encontrada",
        adjustFilter: "Ajuste a busca ou o filtro de status.",
        heroSubtitle: "Conecte contas de anúncio para visualizar campanhas e métricas",
        syncAll: "Sincronizar todas",
        emptyTitle: "Nenhuma conta de anúncios conectada",
        emptyBody: "Conecte sua primeira conta Meta Ads para acompanhar investimento e campanhas.",
        emptyCta: "Conectar Meta Ads",
        wait: "Aguarde",
        syncNow: "Sincronizar agora",
        toastNoAppId: "Facebook App ID não configurado",
        toastPopupBlocked: "Popup bloqueado. Permita popups para este site.",
        toastLoginCancelled: "Login cancelado ou negado",
        toastFetchingAccounts: "Buscando contas de anúncio...",
        toastFetchError: "Erro ao buscar contas",
        toastNoAccounts: "Nenhuma conta de anúncio encontrada neste perfil",
        toastConnectError: "Erro ao conectar",
        toastSaveError: "Erro ao salvar conexão",
        toastSynced: "Contas sincronizadas",
        toastRenamed: "Conta renomeada",
        toastRenameError: "Erro ao renomear",
        toastRemoveError: "Erro ao remover",
        toastDefaultError: "Erro ao definir conta padrão",
        lastSync: "Última sync",
        moreActions: "Mais ações",
        investment30d: "Investimento (30d)",
        tokenExpiredReconnect: "Token expirado — reconecte",
        filterAll: "Todas",
        filterConnected: "Conectadas",
        spendLast30: "Gasto (30 dias)",
        activeCampaigns: "Campanhas ativas",
    },

    integrationVerticals: {
        ALL: "Todas",
        ECOMMERCE: "E-commerce",
        DROPSHIPPING: "Dropshipping",
        INFOPRODUTO: "Infoproduto",
        NUTRACEUTICO: "Nutracêutico",
    } as Record<string, string>,

    integrationDescriptions: {} as Record<string, string>,

    integrationTabs: {
        PLATAFORMAS: "Plataformas",
        CHECKOUTS: "Checkouts",
        GATEWAY: "Pagamentos",
        SOURCING: "Fornecedores",
        LOGISTICS: "Logística",
        ATENDIMENTO: "Atendimento",
        META_ADS: "Meta Ads",
    } as Record<string, string>,

    metaAds: {
        pageTitle: "Meta Ads",
        pageSubtitle: "Campanhas e métricas das suas contas de anúncio",
        emptyTitle: "Nenhuma conta de anúncios conectada",
        emptyBody: "Conecte uma conta de anúncios Meta na página de Integrações para ver os relatórios aqui.",
        presetToday: "Hoje",
        presetYesterday: "Ontem",
        presetWeek: "Semana",
        presetMonth: "Mês",
        presetYear: "Ano",
        kpiSpend: "Gasto Total",
        kpiSpendTip: "Total investido no período selecionado",
        kpiVsPeriod: "Variação vs.",
        kpiImpressions: "Impressões",
        kpiImpressionsTip: "Número de vezes que os anúncios foram exibidos",
        kpiClicks: "Cliques",
        kpiClicksTip: "Total de cliques nos anúncios (todos os tipos). CTR = Cliques ÷ Impressões",
        kpiReach: "Alcance",
        kpiReachTip: "Número de pessoas únicas que viram pelo menos um anúncio",
        kpiCpcSub: "Custo por clique",
        kpiCpcTip: "Custo médio por clique — Gasto ÷ Cliques",
        kpiCpmSub: "Por mil impressões",
        kpiCpmTip: "Custo por mil impressões — eficiência de entrega",
        kpiCppSub: "Por mil alcançados",
        kpiCppTip: "Custo por mil pessoas alcançadas — eficiência vs audiência única",
        kpiFrequency: "Frequência",
        kpiFrequencySub: "Impressões por pessoa",
        kpiFrequencyTip:
            "Média de vezes que cada pessoa viu os anúncios. Acima de 3–4 pode indicar fadiga criativa",
        tabAccount: "Conta",
        tabCampaigns: "Campanhas",
        tabAdSets: "Conjuntos",
        tabAds: "Anúncios",
        clickToPause: "Clique para pausar",
        clickToActivate: "Clique para ativar",
        statusActive: "Ativo",
        statusPaused: "Pausado",
        statusArchived: "Arquivado",
        saveFilters: "Salvar filtros atuais",
        columns: "Colunas",
    },

    /** Sobrescrita por `id` de coluna do dashboard (chave estável, não o texto em PT). */
    /**
     * Erros de Server Action que chegam à TELA.
     *
     * Por que existe (crítica adversarial, 2026-08-11): o cliente faz
     * `toast.error(result.error ?? tp.algoTraduzido)` em 10 sítios — e no caminho de falha
     * a action SEMPRE devolve string, então o fallback traduzido nunca é usado e o
     * português da action é o que o revisor vê. Traduzir só o `??` dava cobertura de
     * fachada. `src/app/actions/` estava fora do escopo do scanner pela justificativa
     * "sem texto de tela", que era falsa.
     */
    actionErrors: {
        notAuthenticated: "Não autenticado",
        storeNotFound: "Loja não encontrada",
        noIntegrationPermission: "Sem permissão para gerenciar integrações",
        connectionNotFound: "Conexão não encontrada na loja",
        invalidConnection: "Conexão inválida",
        sessionExpired: "Sessão de conexão expirada. Conecte novamente com o Facebook.",
        tokenExpired: "Token expirado",
    },

    metaAdsPanels: {
        // Barra de ação em massa — só renderiza com `META_ADS_WRITE_ENABLED`, que hoje
        // está desligado (o `ads_management` não foi submetido). Traduzido mesmo assim
        // para não voltar a ser português esquecido quando a permissão for aprovada.
        selectedCount: "selecionado(s)",
        activate: "Ativar",
        pause: "Pausar",
        bulkToggleFailed: (n: number) => `${n} item(ns) não puderam ser alterados`,
        toggleFailed: (motivo: string) => `Falha ao alterar status: ${motivo}`,
        objTraffic: "Tráfego",
        objConversions: "Conversões",
        rankAbove: "Acima",
        rankAverage: "Médio",
        adPreview: "Prévia do anúncio",
        copyAdName: "Copiar nome do anúncio",
        video: "Vídeo",
        noPreview: "Sem prévia disponível",
        impressions: "Impressões",
        noMetricsPeriod: "Sem métricas para o período selecionado",
        noRankingData: "Sem dados: Meta requer ≥ 500 impressões nos últimos 35 dias de entrega ativa",
        metricsGuide: "Guia de Métricas",
        midRange: "Faixa intermediária",
        twoAdsMin: "≥ 2 anúncios",
        relevanceRankings: "Rankings de Relevância",
        impressions500: "≥ 500 impressões",
        last35Days: "nos últimos 35 dias de entrega ativa",
        convRankOnly: "disponível apenas em campanhas otimizadas para conversão",
        dashMeaning: "= volume insuficiente ou anúncio pausado há mais de 35 dias",
        videoMetrics: "Métricas de Vídeo",
        imageOrLink: "Anúncio de imagem ou link",
        noVideoCreative: "(sem criativo de vídeo)",
        videoNoViews: "Vídeo entregue, mas sem visualizações no período",
        purchaseAttribution: "Atribuição de Compras",
        attributionWindow: "7 dias após clique, 1 dia após visualização",
        campaignIdTip: "Identificador único da campanha na API da Meta",
        yesterdayFixed: "dados de ontem · horário fixo",
        warning: "Atenção",
        conversionFunnel: "Funil de Conversão",
        avgLtv: "LTV Médio",
        productNameTip: "Nome do produto em pedidos com UTM source Meta no período",
        unitsSoldTip: "Total de unidades vendidas via Meta Ads no período",
        productRevenueTip: "Receita total gerada por este produto em pedidos atribuídos ao Meta",
        productShareTip: "Participação deste produto na receita total atribuída ao Meta Ads",
        cohortRetention: "Retenção por Coorte",
        cohort: "Coorte",
        cohortTip: "Mês em que o cliente fez sua primeira compra via Meta Ads",
        size: "Tamanho",
        sizeTip: "Número de clientes adquiridos via Meta neste mês de coorte",
        m0Tip: "Mês de aquisição — sempre 100%",
        m1Tip: "% que realizou nova compra 1–2 meses após a primeira compra via Meta",
        m3Tip: "% que realizou nova compra 2–5 meses após a primeira compra via Meta",
        m6Tip: "% que realizou nova compra 5–8 meses após a primeira compra via Meta",
        creativeAnalysis: "Análise de Criativos",
        creative: "Criativo",
        creativeTip: "Nome do anúncio na Meta Ads",
        campaign: "Campanha",
        campaignParentTip: "Campanha pai do anúncio",
        spend: "Gasto",
        spendAdTip: "Investimento total no período para este anúncio.",
        cpcTip: "Custo por clique médio no período.",
        campaignUtmTip: "Pedidos com UTM desta campanha no período",
        noCampaignsPeriod: "Nenhuma campanha encontrada para o período",
        adSetSpendTip: "Total investido pelo conjunto no período",
        ctrTip: "Taxa de cliques — Cliques ÷ Impressões",
        adSingular: "anúncio",
        adPlural: "anúncios",
        searchAd: "Buscar anúncio...",
        adIdTip: "Identificador único do anúncio no Meta Ads",
        videoAssetTip: "Identificador do asset de vídeo usado neste anúncio",
        viewInMetaAds: "Ver no Meta Ads →",
        howToRead: "Como interpretar os dados da tabela",
        colorSystem: "Sistema de Cores",
        topThird: "do conjunto — melhor desempenho relativo",
        bottomThird: "do conjunto — pior desempenho relativo",
        hookRateNote: "Hook Rate usa 3-Second Video Views — rastreado para todos os objetivos de campanha, diferente do 2-Second Continuous que funciona apenas com objetivo Video Views.",
        clickToExpand: "Clique para expandir",
        clickToCollapse: "Clique para recolher",
        sortActiveFirst: "Clique para inverter (Ativos primeiro)",
        sortPausedFirst: "Clique para inverter (Pausados primeiro)",
        sortByStatus: "Ordenar por status (Ativos primeiro)",
        noAdsFound: "Nenhum anúncio encontrado",
        noData: "Sem dados",
        insightsError: "Erro ao buscar insights",
        unknownError: "Erro desconhecido",
        noConnectionTitle: "Nenhuma conta Meta Ads conectada",
        connectNow: "Conectar agora",
        filterNamePlaceholder: "Nome do filtro...",
        integrationsMetaAds: "Integrações → Meta Ads",
        viewCampaigns: "Ver campanhas",
        campaignMapping: "Mapeamento de Campanhas",
        utmOverride: "UTM override por Campaign ID",
        noActiveCampaignToMap: "Nenhuma campanha ativa para mapear",
        campaignNameTip: "Nome da campanha na Meta Ads",
        utmForceTip: "Força um valor de utm_campaign específico para esta campanha, sobrepondo o fuzzy matching automático",
        fuzzyPriority: "Prioridade sobre matching fuzzy por nome",
        mappingSaveError: "Erro ao salvar mapeamento",
        dailyBrief: "Brief Diário por Email",
        sendTime: "Horário de envio",
        briefSendError: "Erro ao enviar brief",
        topCampaigns: "Top Campanhas",
        lowestRoas: "ROAS mais baixo",
        realAttribution: "Atribuição Real",
        realAttributionTip: "Pedidos com UTM Meta no período · ROAS = Receita Prisma ÷ Gasto Meta",
        funnelTip: "Etapas de conversão do período via Meta Pixel: Cliques → Add to Cart → Checkout → Compra",
        rfmTip: "Segmentação RFM, risco de churn e LTV médio dos clientes que compraram via Meta no período",
        churnRisk: "Risco de Churn",
        productsViaMeta: "Produtos via Meta Ads",
        productsTip: "Produtos mais vendidos em pedidos com UTM source Meta — ordenados por receita, top 10 por padrão",
        showLess: "Ver menos",
        partnerCampaigns: "Campanhas de Parceiros no Período",
        partnerCampaignsTip: "Campanhas de parceiros com período sobreposto ao selecionado e a receita Meta atribuída a cada uma via UTM",
        noUtmMatch: "sem UTM match",
        cohortTableTip: "Taxa de recompra por mês de aquisição via Meta Ads — M+1: 1–2 meses, M+3: 2–5 meses, M+6: 5–8 meses após a primeira compra",
        noRetentionData: "Dados de retenção não disponíveis para o período selecionado",
        noCreativeFound: "Nenhum criativo encontrado para o período",
        ctrCreativeTip: "Taxa de cliques (Clicks ÷ Impressões). Disponível para qualquer período histórico.",
        roasCreativeTip: "Retorno sobre investimento (Receita de compras ÷ Gasto). Disponível para qualquer período histórico. Requer pixel de compra configurado.",
        qualityRankTip: "Quality Ranking da Meta: posição do criativo vs. concorrentes no mesmo público-alvo. Disponível apenas para anúncios em veiculação ativa (não funciona para períodos históricos).",
        engRankTip: "Engagement Rate Ranking: taxa de engajamento comparada a anúncios com o mesmo objetivo. Disponível apenas para anúncios em veiculação ativa (não funciona para períodos históricos).",
        convRankTip: "Conversion Rate Ranking: taxa de conversão comparada a anúncios concorrentes com o mesmo objetivo. Disponível apenas para anúncios em veiculação ativa (não funciona para períodos históricos).",
        scoreTip: "Pontuação composta 0–6 pts: ABOVE_AVERAGE = 2, AVERAGE = 1, BELOW_AVERAGE = 0 — soma de Qualidade + Engajamento + Conversão. Requer anúncio em veiculação ativa.",
        campaigns: "Campanhas",
        clickCampaignHint: "Clique em uma campanha para ver seus conjuntos de anúncios",
        hideEmpty: "Ocultar sem dados",
        searchCampaign: "Buscar campanha...",
        realRevenueTip: "Receita real de pedidos com UTM Meta (via Prisma)",
        realRoasTip: "ROAS Real = Receita Prisma ÷ Gasto Meta desta campanha",
        orders: "Pedidos",
        roasGapTip: "Discrepância entre ROAS Meta Pixel e ROAS Real (Prisma). Positivo = Meta subestimou; negativo = Meta sobreavaliou.",
        noCampaignMatchesFilter: "Nenhuma campanha corresponde ao filtro",
        noAdSetForPeriod: "Nenhum conjunto encontrado para este período",
        searchAdSet: "Buscar conjunto...",
        noAdSetMatchesFilter: "Nenhum conjunto corresponde ao filtro",
        pickCampaignForAdSets: "Selecione uma campanha para ver os conjuntos de anúncios",
        goToCampaigns: "Ir para Campanhas →",
        pickAdSetForAds: "Selecione um conjunto para ver os anúncios",
        tokenExpiredHere: "O token desta conta expirou. Reconecte em",
        frequencyAt: (v: string) => `Frequência em ${v} — risco de fadiga criativa. Considere renovar os criativos.`,
        tokenExpiresIn: (d: string) => `Token Meta Ads expira em ${d}.`,
        reconnect: "Reconectar",
        settings: "Configurações",
        expiresIn: (d: string) => `Expira em ${d}`,
        day: "dia",
        days: "dias",
        saveMapping: "Salvar mapeamento",
        utmCoverage: (m: string, t: string) => `${m} de ${t} pedidos têm UTM Meta rastreável`,
        cumulativeRate: ", com taxa acumulada entre cada etapa",
        ordersCount: (n: string) => `${n} pedidos`,
        loadMore: (n: string) => `Carregar mais ${n} campanhas`,
        ofTotal: (a: string, b: string) => `(${a} de ${b})`,
        qualityCol: "Qualidade",
        engagementCol: "Engaj.",
        conversionCol: "Convers.",
        allObjectives: "Todos",
        objSales: "Vendas",
        objLeads: "Leads",
        objEngagement: "Engajamento",
        objApp: "App",
        objAwareness: "Reconhecimento",
        objPageLikes: "Curtidas",
        objReach: "Alcance",
        objLinkClicks: "Cliques",
        rankBelow10: "< 10%",
        colorNote: "Cores comparam anúncios dentro do mesmo conjunto — um valor vermelho aqui pode ser verde em outro conjunto. Para CPC / CPA / CPV menor é melhor; para CTR / ROAS / Compras maior é melhor. Requer ≥ 2 anúncios com dados para ativar.",
        rankRule1: "Requerem ≥ 500 impressões nos últimos 35 dias de entrega ativa",
        rankRule2: "Conv. Rank. disponível apenas em campanhas otimizadas para conversão",
        rankRule3: "— = volume insuficiente ou anúncio pausado há mais de 35 dias",
        attributionNote: "Compras · Receita · CPA · ROAS dependem do Meta Pixel ou Conversions API configurados na conta. Janela padrão: 7 dias após clique, 1 dia após visualização. A janela pode ser alterada nas configurações do conjunto de anúncios.",
        fatigueFrequency: "Frequência",
        fatigueCritical: "⚡ Fadiga Crítica",
        fatigueDetected: "⚠️ Fadiga Detectada",
        frequencyUp: "Frequência ↑",
        configSaved: "Configuração salva",
        configSaveError: "Erro ao salvar configuração",
    },

    metaAdsColumns: {} as Record<string, { label: string; tooltip?: string }>,
};

/**
 * Sem `as const` de propósito: com literais congelados, `en` teria de repetir os MESMOS
 * textos em português para satisfazer o tipo. O que precisamos travar é a forma, não o valor.
 */
export type Dictionary = typeof pt;
